/**
 * Entities query hooks using generic factory
 */
import type { EntityFilters } from '@/store/entity-filters.store'
import { entityApi } from '@/lib/api/entities.api'
import { normalizeEntityLifecycleStageFilter } from '@/store/entity-filters.store'

import type { Entity } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { createResourceQueries } from './createResourceQuery'

const entitiesQueryConfig = {
  queryKeyNamespace: queryKeys.entities.all,
  api: {
    getList: async (filters?: EntityFilters) => {
      const requestFilters: EntityFilters & {
        includeCustomFields: boolean
        columnFilters?: Record<string, unknown>
      } = {
        includeCustomFields:
          (filters as { includeCustomFields?: boolean } | undefined)
            ?.includeCustomFields ?? true,
        ...filters,
      }

      const params: Record<string, unknown> = {
        page: requestFilters.page || 1,
        limit: requestFilters.limit || 50,
        sortBy: requestFilters.sortBy,
        sortOrder: requestFilters.sortOrder,
        includeCustomFields: requestFilters.includeCustomFields,
      }

      if (requestFilters.name && requestFilters.name.trim() !== '') {
        params.name = requestFilters.name.trim()
      }

      if (
        requestFilters.entityTypeId &&
        requestFilters.entityTypeId.trim() !== ''
      ) {
        params.entityTypeId = requestFilters.entityTypeId
      }

      // T2 record-state opt-in: unset = server default (active + provisional,
      // merged/archived tombstones hidden); 'all' or an explicit state opts in.
      if (requestFilters.recordState) {
        params.recordState = requestFilters.recordState
      }

      // Merge column filters with the lifecycle quick-filter chip (Path C).
      const mergedColumnFilters: Record<string, unknown> = {
        ...((requestFilters.columnFilters as
          | Record<string, unknown>
          | undefined) ?? {}),
      }
      const lifecycleStageIds = normalizeEntityLifecycleStageFilter(
        requestFilters.lifecycleStage,
      )
      if (lifecycleStageIds.length > 0) {
        mergedColumnFilters.lifecycleStage = lifecycleStageIds
      }
      if (Object.keys(mergedColumnFilters).length > 0) {
        params.columnFilters = JSON.stringify(mergedColumnFilters)
      }

      return entityApi.getAll(params)
    },
    getDetail: (id: string) => entityApi.getById(id),
  },
  requiresOrganization: true,
}

const { useListQuery, useDetailQuery } = createResourceQueries<
  Entity,
  EntityFilters
>(entitiesQueryConfig, queryKeys.entities)

export const useEntitiesQuery = useListQuery
export const useEntityQuery = useDetailQuery

/**
 * useEntitiesQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const entitiesListQuery = useEntitiesQuery
